import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, AlertTriangle, Clock } from 'lucide-react';
const DURATION_MS = 30 * 60 * 1000; // 30 minutes
const STORAGE_KEY = 'mp_oferta_deadline';
const START_PCT = 77;
const MAX_PCT = 95;
function getDeadline(): number {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!Number.isNaN(ts) && ts > Date.now()) return ts;
    }
  } catch {
    /* ignore */
  }
  const deadline = Date.now() + DURATION_MS;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(deadline));
  } catch {
    /* ignore */
  }
  return deadline;
}
function format(n: number) {
  return n.toString().padStart(2, '0');
}
export function UrgencyBanner() {
  const [remaining, setRemaining] = useState<number>(DURATION_MS);
  useEffect(() => {
    const deadline = getDeadline();
    const tick = () => {
      setRemaining(Math.max(0, deadline - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const elapsedFraction = (DURATION_MS - remaining) / DURATION_MS;
  const pct = Math.min(MAX_PCT, START_PCT + elapsedFraction * (MAX_PCT - START_PCT));
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="max-w-3xl mx-auto mb-6 md:mb-8" data-id="element-296">
      <div className="relative rounded-2xl bg-red-600 text-white shadow-xl shadow-red-600/30 border-2 border-amber-400 overflow-hidden" data-id="element-297">
        {/* animated alert stripe */}
        <motion.div aria-hidden="true" animate={{
        opacity: [0.35, 0.9, 0.35]
      }} transition={{
        repeat: Infinity,
        duration: 1.8,
        ease: 'easeInOut'
      }} className="absolute inset-x-0 top-0 h-1 bg-amber-400" data-id="element-298" />

        <div className="p-4 md:p-5" data-id="element-299">
          {/* Top: headline + timer */}
          <div className="flex items-center justify-between gap-3 mb-3" data-id="element-300">
            <div className="flex items-center gap-2 min-w-0" data-id="element-301">
              <motion.span animate={{
              scale: [1, 1.18, 1]
            }} transition={{
              repeat: Infinity,
              duration: 1.2,
              ease: 'easeInOut'
            }} className="flex-shrink-0" data-id="element-302">
                <Flame className="h-5 w-5 md:h-6 md:w-6 text-amber-300" aria-hidden="true" data-id="element-303" />
              </motion.span>
              <span className="font-extrabold text-sm md:text-lg uppercase tracking-tight leading-none truncate" data-id="element-304">
                ¡Últimos cupos de lanzamiento!
              </span>
            </div>

            <div className="flex-shrink-0 flex items-center gap-1.5 bg-slate-900 rounded-lg px-2.5 py-1.5 md:px-3 md:py-2" aria-label={`Tiempo restante ${format(minutes)} minutos ${format(seconds)} segundos`} data-id="element-305">
              <Clock className="h-3.5 w-3.5 md:h-4 md:w-4 text-amber-400" aria-hidden="true" data-id="element-306" />
              <span className="font-mono font-black text-base md:text-2xl tabular-nums tracking-wider" data-id="element-307">
                {format(minutes)}:{format(seconds)}
              </span>
            </div>
          </div>

          {/* Warning message */}
          <p className="flex items-start gap-1.5 text-xs md:text-sm font-semibold text-amber-100 mb-3 leading-snug" data-id="element-308">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-300" aria-hidden="true" data-id="element-309" />
            <span data-id="element-310">
              Cuando se agoten los cupos, el precio sube{' '}
              <strong className="text-white font-extrabold" data-id="element-311">+$10.000</strong> y{' '}
              <strong className="text-white font-extrabold" data-id="element-312">
                no vuelve a bajar
              </strong>
              .
            </span>
          </p>

          {/* Progress / cupos bar */}
          <div data-id="element-313">
            <div className="flex items-center justify-between text-[10px] md:text-xs font-bold mb-1" data-id="element-314">
              <span className="text-amber-100 uppercase tracking-wide" data-id="element-315">
                Cupos ocupados
              </span>
              <span className="text-white tabular-nums" data-id="element-316">
                {Math.round(pct)}%
              </span>
            </div>
            <div className="h-2.5 md:h-3 w-full rounded-full bg-red-900/60 overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label="Cupos ocupados" data-id="element-317">
              <motion.div className="h-full rounded-full bg-amber-400" initial={{
              width: `${START_PCT}%`
            }} animate={{
              width: `${pct}%`
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} data-id="element-318" />
            </div>
            <p className="text-[10px] md:text-xs text-amber-100 mt-1 font-medium" data-id="element-319">
              Quedan pocos lugares a este precio.
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}